import React, { FC, useState, useCallback, useRef } from 'react'
import { View, Text, StyleSheet, ViewStyle } from 'react-native'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import { Hoverable } from 'react-native-web-hooks'
import { createPortal } from 'react-dom'
import { BtText } from '@linqpal/components/src/ui'

interface ITooltipIconProps {
  tooltip: any
  title?: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  size?: number
  iconName?: string
  iconColor?: string
  hoverColor?: string
  defaultColor?: string
  useHoverEffect?: boolean
  containerStyle?: ViewStyle
  tooltipWidth?: number
}

export const TooltipIconModal: FC<ITooltipIconProps> = ({
  tooltip,
  title,
  position = 'bottom',
  size = 12,
  iconName = 'information-outline',
  iconColor,
  hoverColor = '#00A0F3',
  defaultColor = '#00A0F3',
  useHoverEffect = false,
  containerStyle,
  tooltipWidth = 246,
}) => {
  const [visible, setVisible] = useState(false)
  const [tooltipElement, setTooltipElement] = useState<HTMLElement | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout>()

  const handleRef = useCallback((node: HTMLElement | null) => {
    if (node !== null) {
      setTooltipElement(node)
    }
  }, [])

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setVisible(true)
  }
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setVisible(false)
    }, 100)
  }
  const renderTooltip = () => {
    if (!visible || !tooltipElement) return null

    const rect = tooltipElement.getBoundingClientRect()
    const arrowSize = 8
    const tooltipHeight = 100

    const iconCenterX = rect.left + rect.width / 2
    const iconCenterY = rect.top + rect.height / 2

    const getPositionStyles = () => {
      const transparent = `${arrowSize}px solid transparent`
      const arrowColor = `${arrowSize}px solid #003353`
      const halfTooltip = tooltipWidth / 2
      const halfHeight = tooltipHeight / 2

      switch (position) {
        case 'top':
          return {
            tooltip: {
              left: iconCenterX - halfTooltip,
              bottom: window.innerHeight - rect.top + 8,
            },
            arrow: {
              borderLeft: transparent,
              borderRight: transparent,
              borderTop: arrowColor,
              bottom: -arrowSize,
              left: halfTooltip - arrowSize,
            },
          }

        case 'left':
          return {
            tooltip: {
              right: window.innerWidth - rect.left + 8,
              top: iconCenterY - halfHeight,
            },
            arrow: {
              borderTop: transparent,
              borderBottom: transparent,
              borderLeft: arrowColor,
              right: -arrowSize,
              top: halfHeight - arrowSize,
            },
          }

        case 'right':
          return {
            tooltip: {
              left: rect.right + 8,
              top: iconCenterY - halfHeight,
            },
            arrow: {
              borderTop: transparent,
              borderBottom: transparent,
              borderRight: arrowColor,
              left: -arrowSize,
              top: halfHeight - arrowSize,
            },
          }

        case 'bottom':
        default:
          return {
            tooltip: {
              left: iconCenterX - halfTooltip,
              top: rect.bottom + 8,
            },
            arrow: {
              borderLeft: transparent,
              borderRight: transparent,
              borderBottom: arrowColor,
              top: -arrowSize,
              left: halfTooltip - arrowSize,
            },
          }
      }
    }

    const { tooltip: tooltipPositionStyle, arrow: arrowPositionStyle } =
      getPositionStyles()
    const tooltipContent = (
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          position: 'fixed',
          width: tooltipWidth,
          backgroundColor: '#003353',
          borderRadius: 4,
          padding: 16,
          zIndex: 2147483647,
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
          ...tooltipPositionStyle,
        }}
      >
        <div
          style={{
            position: 'absolute',
            width: 0,
            height: 0,
            ...arrowPositionStyle,
          }}
        />
        {title && (
          <View style={styles.titleContainer}>
            <Text style={styles.text}>{title}</Text>
          </View>
        )}
        <BtText weight={'400'} style={styles.text}>
          {tooltip}
        </BtText>
      </div>
    )

    return createPortal(tooltipContent, document.body)
  }

  const getIconColor = (isHovered: boolean) => {
    if (iconColor) {
      return iconColor
    }

    if (useHoverEffect) {
      return isHovered ? hoverColor : defaultColor
    } else {
      return defaultColor
    }
  }
  return (
    <View style={[styles.container, containerStyle]}>
      <Hoverable onHoverIn={handleMouseEnter} onHoverOut={handleMouseLeave}>
        {(isHovered) => (
          <div ref={handleRef}>
            <MaterialCommunityIcons
              name={iconName}
              size={size}
              color={getIconColor(isHovered)}
            />
          </div>
        )}
      </Hoverable>
      {renderTooltip()}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    display: 'flex',
  },
  titleContainer: {
    marginBottom: 8,
  },
  text: {
    fontFamily: 'Inter, sans-serif',
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '400',
  },
})
